import type { AppRoutes } from "@app/appRouter.ts";
import { AppTitle } from "@app/components/AppTitle.tsx";
import { Button } from "@app/components/shadcn/button.tsx";
import { ThemeToggleButton } from "@app/components/ThemeToggleButton.tsx";
import { useNavigationItems } from "@app/hooks/useNavigationItems.ts";
import { useAuthStore } from "@app/stores/useAuthStore.ts";
import { Link, useLocation } from "@tanstack/react-router";
import { User } from "lucide-react";
import type { ComponentType, JSX, PropsWithChildren } from "react";

export type AppScaffoldProps = PropsWithChildren<{
  hideNavigationBar: boolean;
}>;

export const AppScaffold = ({
  hideNavigationBar,
  children,
}: AppScaffoldProps) => {
  const navigationItems = useNavigationItems({ enabled: !hideNavigationBar });
  const contentClassName = hideNavigationBar
    ? "flex-5 overflow-y-auto [scrollbar-gutter:stable] w-full h-full"
    : "app-blur-surface flex-5 overflow-y-auto [scrollbar-gutter:stable] w-full h-full border-y md:rounded-xl md:border";

  return (
    <div className="flex flex-col md:flex-row h-screen w-screen md:gap-4 md:p-4">
      {hideNavigationBar ? null : (
        <div className="app-blur-surface hidden md:flex flex-2 max-w-75 w-full h-full overflow-hidden rounded-xl border">
          <DesktopNavigationBar items={navigationItems} />
        </div>
      )}

      {hideNavigationBar ? null : (
        <div className="flex md:hidden">
          <MobileHeader />
        </div>
      )}

      <div className={contentClassName}>{children}</div>

      {hideNavigationBar ? null : (
        <div className="flex md:hidden">
          <MobileNavigationBar items={navigationItems} />
        </div>
      )}
    </div>
  );
};

export type NavigationBarItem = {
  label: string;
  to: AppRoutes;
  icon: ComponentType<JSX.IntrinsicElements["svg"]>;
  activePathnamePrefixes: string[];
};

const accountItem: NavigationBarItem = {
  label: "Conta",
  to: "/account",
  icon: User,
  activePathnamePrefixes: ["/account"],
};

function useActiveClassName(item: NavigationBarItem) {
  const { pathname } = useLocation();
  return item.activePathnamePrefixes.some((prefix) =>
    pathname.startsWith(prefix),
  )
    ? " active"
    : "";
}

type DesktopNavigationBarProps = {
  items: NavigationBarItem[];
};

const DesktopNavigationBar = ({ items }: DesktopNavigationBarProps) => {
  const userEmail = useAuthStore((store) => store.user?.email);

  return (
    <div className="flex flex-col items-stretch w-full p-4 h-full">
      <div className="flex items-center justify-between gap-2 pb-4 mb-2 w-full border-b border-border/60">
        <AppTitle />
        <ThemeToggleButton />
      </div>

      <nav className="flex flex-col gap-1">
        {items.map((item) => (
          <DesktopNavigationLink key={item.label} item={item} />
        ))}
      </nav>

      <div className="mt-auto pt-2 border-t border-border/60">
        <DesktopNavigationLink item={accountItem} detail={userEmail} />
      </div>
    </div>
  );
};

type DesktopNavigationLinkProps = {
  item: NavigationBarItem;
  detail?: string | undefined;
};

const DesktopNavigationLink = ({
  item,
  detail,
}: DesktopNavigationLinkProps) => {
  const activeClassName = useActiveClassName(item);

  return (
    <Link
      to={item.to}
      className={`relative flex items-center w-full gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary/10 [&.active]:text-primary [&.active]:font-semibold${activeClassName}`}
    >
      <span className="absolute left-0 top-1/2 h-0 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-all in-[.active]:h-5" />
      <item.icon className="h-4 w-4 shrink-0" />
      <span className="flex min-w-0 flex-col">
        {item.label}
        {detail ? (
          <span className="truncate text-xs font-normal text-muted-foreground">
            {detail}
          </span>
        ) : null}
      </span>
    </Link>
  );
};

const MobileHeader = () => {
  const activeClassName = useActiveClassName(accountItem);

  return (
    <header className="flex w-full items-center justify-between gap-2 px-4 py-2">
      <AppTitle />
      <div className="flex items-center gap-2">
        <ThemeToggleButton />
        <Button
          asChild
          variant="outline"
          size="icon"
          className="[&.active]:border-primary [&.active]:text-primary"
        >
          <Link to={accountItem.to} className={activeClassName}>
            <accountItem.icon />
            <span className="sr-only">{accountItem.label}</span>
          </Link>
        </Button>
      </div>
    </header>
  );
};

type NavigationBarProps = {
  items: NavigationBarItem[];
};

export const MobileNavigationBar = ({ items }: NavigationBarProps) => {
  return (
    <nav className="app-blur-surface flex flex-row w-full h-16 items-center justify-around border-t rounded-t-2xl">
      {items.map((item) => (
        <MobileNavigationLink key={item.label} item={item} />
      ))}
    </nav>
  );
};

const MobileNavigationLink = ({ item }: { item: NavigationBarItem }) => {
  const activeClassName = useActiveClassName(item);

  return (
    <Link
      to={item.to}
      className={`relative flex flex-1 flex-col items-center gap-1 text-xs font-medium text-muted-foreground transition-colors [&.active]:text-primary [&.active]:font-semibold${activeClassName}`}
    >
      <span className="absolute -top-2 h-1 w-1 rounded-full bg-transparent in-[.active]:bg-primary" />
      <item.icon className="h-5 w-5" />
      {item.label}
    </Link>
  );
};
